/**
 * `JobMatchCard` — card di una professione (Job e-CF 3.0) che mostra il
 * posizionamento dello User, riusando lo **stesso markup e stile** delle
 * `CompetencyMatchCard` della Dashboard: donut della Copertura in alto a destra,
 * barra della Proficiency, footer con il conteggio, hover animato e click su
 * tutta la card (apre `/jobs/:id`).
 *
 * Il modificatore `--job` rimuove l'accento sul bordo sinistro (specifico delle
 * card di dashboard). La variante `compact` rende una riga orizzontale, come le
 * competenze "altre" nella Dashboard.
 */
import type { KeyboardEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import type { JobMatch } from '../shared/dto';
import { scoreColor } from './StarRating';

/** Formatta una percentuale `0..100` come intero con `%`. */
function formatPercent(value: number): string {
  return `${Math.round(value)}%`;
}

const DONUT_RADIUS = 16;
const DONUT_CIRCUMFERENCE = 2 * Math.PI * DONUT_RADIUS;

/** Anello di progresso di una percentuale `0..100` (Copertura del Job). */
function ScoreDonut({ score, label }: { score: number; label: string }) {
  const clamped = Math.min(100, Math.max(0, score));
  const rounded = Math.round(clamped);
  const ringColor = scoreColor(clamped / 10) ?? 'var(--color-primary)';
  const dash = (clamped / 100) * DONUT_CIRCUMFERENCE;

  return (
    <div
      className="competency-match-card__donut"
      role="img"
      aria-label={`${label} ${rounded} su 100`}
      title={`${label} ${rounded}/100`}
    >
      <svg viewBox="0 0 36 36" className="competency-match-card__donut-svg" aria-hidden="true">
        <circle className="competency-match-card__donut-track" cx="18" cy="18" r={DONUT_RADIUS} />
        <circle
          className="competency-match-card__donut-fill"
          cx="18"
          cy="18"
          r={DONUT_RADIUS}
          stroke={ringColor}
          strokeDasharray={`${dash} ${DONUT_CIRCUMFERENCE - dash}`}
          transform="rotate(-90 18 18)"
        />
      </svg>
      <span className="competency-match-card__donut-value" aria-hidden="true">
        {rounded}
      </span>
    </div>
  );
}

/** Barra di progresso etichettata `0..100` (o `null` → trattino). */
function MetricBar({ label, value }: { label: string; value: number | null }) {
  if (value === null) {
    return (
      <div className="competency-match-card__metric">
        <dt>{label}</dt>
        <dd>
          <span
            className="competency-match-card__value competency-match-card__value--empty"
            aria-label={`${label} non disponibile`}
          >
            —
          </span>
        </dd>
      </div>
    );
  }
  const clamped = Math.min(100, Math.max(0, value));
  const exact = Math.round(Math.max(0, value));
  const color = scoreColor(clamped / 10);
  return (
    <div className="competency-match-card__metric">
      <dt>{label}</dt>
      <dd>
        <span
          role="progressbar"
          aria-valuenow={exact}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-label={`${label} ${formatPercent(clamped)}`}
          className="competency-match-card__bar"
        >
          <span
            className="competency-match-card__bar-fill"
            style={{ width: `${clamped}%`, ...(color ? { background: color } : {}) }}
          />
        </span>
        <span className="competency-match-card__value">{formatPercent(clamped)}</span>
      </dd>
    </div>
  );
}

export interface JobMatchCardProps {
  job: JobMatch;
  /** Variante compatta a riga orizzontale (per gli "altri" job in accordion). */
  compact?: boolean;
  /**
   * Costruisce il path di dettaglio della professione a partire dal suo id.
   * Serve a riusare la card sia nella Dashboard personale (`/jobs/:id`) sia nel
   * drill-down del Team (`/team/:userId/jobs/:id`). Se assente, si usa il path
   * della dashboard personale.
   */
  detailPath?: (jobId: string) => string;
  /**
   * Percorso di provenienza propagato come `state.from` sulla navigazione al
   * dettaglio: consente alla pagina di dettaglio di tornare al punto giusto
   * (es. `/assessment` quando si arriva da "Il mio CV") invece che al default
   * ("Le mie professioni"). Assente ⇒ nessuno stato di navigazione.
   */
  backTo?: string;
  /**
   * Se `true` (default) la card è navigazionale: cliccabile e attivabile da
   * tastiera (apre il dettaglio della professione). Con `false` la card è
   * puramente presentazionale (nessun `role`/`tabIndex`/handler né classe
   * `--interactive`), usata per i profili esterni non navigabili.
   */
  interactive?: boolean;
}

export function JobMatchCard({
  job,
  compact = false,
  detailPath,
  backTo,
  interactive = true,
}: JobMatchCardProps) {
  const navigate = useNavigate();

  const openDetail = () => {
    const path = detailPath
      ? detailPath(job.jobId)
      : `/jobs/${encodeURIComponent(job.jobId)}`;
    void navigate(path, backTo !== undefined ? { state: { from: backTo } } : undefined);
  };
  const handleKeyDown = (e: KeyboardEvent<HTMLElement>) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      openDetail();
    }
  };

  // Props di interazione applicate solo quando `interactive` è `true`: così la
  // navigazione (openDetail) non viene mai attivata sulle card statiche.
  const interactiveProps = interactive
    ? {
        role: 'button' as const,
        tabIndex: 0,
        onClick: openDetail,
        onKeyDown: handleKeyDown,
        'aria-label': `Apri il dettaglio della professione ${job.name}`,
      }
    : {};

  const matchChip = (
    <span
      className="competency-match-card__chip"
      aria-label={`Match ${formatPercent(job.matchPercent)}`}
    >
      {formatPercent(job.matchPercent)} match
    </span>
  );

  const coverageMeta = (
    <span
      className="competency-match-card__skills"
      aria-label={`${job.coveredCompetenceCount} di ${job.totalCompetenceCount} competenze coperte`}
    >
      {job.coveredCompetenceCount}/{job.totalCompetenceCount} competenze coperte
    </span>
  );

  // Variante compatta: riga orizzontale (donut a sinistra, nome/meta al centro,
  // proficiency a destra), come le competenze "altre" della Dashboard.
  if (compact) {
    return (
      <article
        className={[
          'competency-match-card',
          'competency-match-card--job',
          'competency-match-card--compact',
          interactive ? 'competency-match-card--interactive' : '',
        ]
          .filter(Boolean)
          .join(' ')}
        {...interactiveProps}
      >
        <ScoreDonut score={job.coveragePercent} label="Copertura" />
        <div className="competency-match-card__main">
          <div className="competency-match-card__title">
            <h3 className="competency-match-card__name">{job.name}</h3>
            {matchChip}
          </div>
          {coverageMeta}
        </div>
        <dl className="competency-match-card__inline-metrics">
          <MetricBar label="Proficiency" value={job.proficiencyPercent} />
        </dl>
      </article>
    );
  }

  return (
    <article
      className={[
        'competency-match-card',
        'competency-match-card--job',
        interactive ? 'competency-match-card--interactive' : '',
      ]
        .filter(Boolean)
        .join(' ')}
      {...interactiveProps}
    >
      {/* Donut della Copertura (ampiezza) nell'angolo in alto a destra. */}
      <ScoreDonut score={job.coveragePercent} label="Copertura" />

      <div className="competency-match-card__header">
        <h3 className="competency-match-card__name">{job.name}</h3>
      </div>
      {job.summary && (
        <p className="competency-match-card__description">{job.summary}</p>
      )}
      <dl className="competency-match-card__metrics">
        <MetricBar label="Proficiency" value={job.proficiencyPercent} />
      </dl>
      {/* Footer: competenze coperte a sinistra, match a destra. */}
      <div className="competency-match-card__footer">
        {coverageMeta}
        {matchChip}
      </div>
    </article>
  );
}

export default JobMatchCard;
